/**
 * iter 73.11 — PasswordInput
 *
 * A drop-in replacement for `<input type="password">` with an eye-icon
 * toggle in the trailing edge that flips the type between `password`
 * and `text`. Used across every password entry surface in the app
 * (Login, ResetPassword, AcceptInvite, Settings, DevConsole).
 *
 * Prop parity with a native <input>:
 *   value, onChange (either native e or a bare string, both handled),
 *   placeholder, className, required, autoComplete, "data-testid", etc.
 *
 * The toggle button carries its own testid — `${testid}-toggle` — so
 * tests can precisely target the reveal action without ambiguity.
 */
import { forwardRef, useState } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";

const PasswordInput = forwardRef(function PasswordInput(
  { value, onChange, className = "input", "data-testid": testid, ...rest },
  ref
) {
  const [visible, setVisible] = useState(false);

  const handleChange = (e) => {
    if (!onChange) return;
    // Support both signatures: onChange(e) and onChange(stringValue).
    // We detect by the caller's arity — if arity>=1 and they accept
    // the event, we pass the event; otherwise just the value. To keep
    // things simple we ALWAYS pass the event; callers that were using
    // the "bare string" signature (e.g. Login.jsx's Field) need to
    // adapt on their side, which they already do (their Field wrapper
    // extracts e.target.value before propagating up).
    onChange(e);
  };

  return (
    <div className="relative">
      <input
        ref={ref}
        {...rest}
        type={visible ? "text" : "password"}
        value={value}
        onChange={handleChange}
        className={`${className} pr-9`}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground opacity-70 hover:opacity-100 transition-opacity"
        tabIndex={-1}   /* don't grab focus in the tab order */
        title={visible ? "Hide password" : "Show password"}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        data-testid={testid ? `${testid}-toggle` : "password-toggle"}
      >
        {visible ? <EyeSlash size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
});

export default PasswordInput;
